import React, { useState } from 'react';
import { 
  Clock, 
  Users, 
  Phone, 
  Mail, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Calendar,
  MessageSquare,
  DollarSign,
  RotateCcw,
  Loader2,
  Hash,
  Copy,
  Check
} from 'lucide-react';
import { Reservation } from '../../types';
import apiService from '../../services/api';

interface ReservationCardProps {
  reservation: Reservation;
  onStatusChange: (id: number, status: string) => void;
  onCancel: (id: number) => void;
  onRefund?: (id: number) => void;
}

const ReservationCard: React.FC<ReservationCardProps> = ({
  reservation,
  onStatusChange,
  onCancel,
  onRefund,
}) => {
  const [isRefunding, setIsRefunding] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const formatDateTime = (dateTime: string) => {
    const date = new Date(dateTime);
    return {
      date: date.toLocaleDateString('pt-PT'),
      time: date.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }),
    };
  };

  const { date, time } = formatDateTime(reservation.data_hora);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmada':
        return 'bg-green-100 text-green-800';
      case 'pendente':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelada':
        return 'bg-red-100 text-red-800';
      case 'concluida':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-secondary-100 text-secondary-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmada':
        return <CheckCircle className="h-4 w-4" />;
      case 'pendente':
        return <AlertCircle className="h-4 w-4" />;
      case 'cancelada':
        return <XCircle className="h-4 w-4" />;
      case 'concluida':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getPaymentStatusColor = (paymentStatus: string) => {
    switch (paymentStatus) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'refunded':
        return 'bg-gray-100 text-gray-800';
      case 'pending_payment':
        return 'bg-orange-100 text-orange-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'not_required':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getPaymentStatusIcon = (paymentStatus: string) => {
    switch (paymentStatus) {
      case 'confirmed':
        return <DollarSign className="h-4 w-4" />;
      case 'refunded':
        return <RotateCcw className="h-4 w-4" />;
      case 'pending_payment':
        return <Clock className="h-4 w-4" />;
      case 'failed':
        return <XCircle className="h-4 w-4" />;
      case 'not_required':
        return null;
      default:
        return null;
    }
  };

  const getPaymentStatusText = (paymentStatus: string) => {
    switch (paymentStatus) {
      case 'confirmed':
        return 'Pago';
      case 'refunded':
        return 'Reembolsado';
      case 'pending_payment':
        return 'Pendente';
      case 'failed':
        return 'Falhado';
      case 'not_required':
        return 'Sem taxa';
      default:
        return 'N/A';
    }
  };

  const handleRefund = async () => {
    try {
      setIsRefunding(true);
      setRefundError(null);
      
      const updatedReservation = await apiService.refundReservation(reservation.id);
      
      // Call the parent callback if provided
      if (onRefund) {
        onRefund(reservation.id);
      }
      
      setShowRefundModal(false);
    } catch (error: any) {
      setRefundError('Falha no reembolso. Tente novamente ou processe manualmente no seu painel EuPago.');
      console.error('Refund error:', error);
    } finally {
      setIsRefunding(false);
    }
  };

  const handleCopyEupagoRef = async () => {
    if (!reservation.internal_payment_id) return;
    
    try {
      await navigator.clipboard.writeText(reservation.internal_payment_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const canChangeStatus = reservation.status !== 'cancelada' && reservation.status !== 'concluida';
  const isPending = reservation.status === 'pendente';
  const canRefund = reservation.payment_status === 'confirmed' && reservation.payment_amount && reservation.payment_amount > 0;

  return (
    <div className="card p-6 hover:shadow-lg transition-shadow">
      <div className="flex flex-col space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center flex-wrap gap-2">
            <h3 className="text-lg font-semibold text-secondary-900">
              {reservation.nome_cliente}
            </h3>
            {/* Reservation ID Badge - Prominent display */}
            <span className="inline-flex items-center space-x-1 rounded-full px-3 py-1 text-sm font-semibold bg-primary-100 text-primary-800 border border-primary-200">
              <Hash className="h-3.5 w-3.5" />
              <span>ID: {reservation.id}</span>
            </span>
            {/* Payment Platform Reservation ID - Copy button */}
            {reservation.internal_payment_id && (
              <button
                onClick={handleCopyEupagoRef}
                className="inline-flex items-center space-x-1 rounded-full px-3 py-1 text-sm font-semibold bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200 transition-colors cursor-pointer"
                title={`Copiar: ${reservation.internal_payment_id}`}
              >
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5" />
                    <span>Copiado!</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" />
                    <span>Eupago Ref.</span>
                  </>
                )}
              </button>
            )}
            <span className={`inline-flex items-center space-x-1 rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(reservation.status)}`}>
              {getStatusIcon(reservation.status)}
              <span className="capitalize">{reservation.status}</span>
            </span>
            {/* Payment Status Badge */}
            {reservation.payment_status && (
              <span className={`inline-flex items-center space-x-1 rounded-full px-2 py-1 text-xs font-medium ${getPaymentStatusColor(reservation.payment_status)}`}>
                {getPaymentStatusIcon(reservation.payment_status)}
                <span>{getPaymentStatusText(reservation.payment_status)}</span>
              </span>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex items-center space-x-2 text-sm text-secondary-600">
            <Calendar className="h-4 w-4" />
            <span>{date}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-secondary-600">
            <Clock className="h-4 w-4" />
            <span>{time}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-secondary-600">
            <Users className="h-4 w-4" />
            <span>{reservation.numero_pessoas} pessoa{reservation.numero_pessoas !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-secondary-600">
            <Phone className="h-4 w-4" />
            <span>{reservation.telefone_cliente}</span>
          </div>
        </div>

        {/* Email and table info if available */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {reservation.email_cliente && (
            <div className="flex items-center space-x-2 text-sm text-secondary-600">
              <Mail className="h-4 w-4" />
              <span>{reservation.email_cliente}</span>
            </div>
          )}
          {reservation.mesa && (
            <div className="flex items-center space-x-2 text-sm text-secondary-600">
              <span className="font-medium">Mesa:</span>
              <span>{reservation.mesa.nome_ou_numero}</span>
            </div>
          )}
        </div>

        {/* Observations */}
        {reservation.observacoes && (
          <div className="bg-secondary-50 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <MessageSquare className="h-4 w-4 text-secondary-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-secondary-700">Observações:</p>
                <p className="text-sm text-secondary-600">{reservation.observacoes}</p>
              </div>
            </div>
          </div>
        )}

        {/* Payment Information */}
        {reservation.payment_amount && reservation.payment_amount > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <DollarSign className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">
                  Taxa de Reserva: €{reservation.payment_amount.toFixed(2)}
                </span>
              </div>
              {reservation.eupago_transaction_ref && (
                <span className="text-xs text-blue-600">
                  Ref: {reservation.eupago_transaction_ref}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          {canChangeStatus && (
            <>
              {isPending && (
                <button
                  onClick={() => onStatusChange(reservation.id, 'confirmada')}
                  className="btn-primary text-sm"
                >
                  Confirmar
                </button>
              )}
              {reservation.status === 'confirmada' && (
                <button
                  onClick={() => onStatusChange(reservation.id, 'concluida')}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-1 px-3 rounded text-sm transition-colors"
                >
                  Marcar como Concluída
                </button>
              )}
              <button
                onClick={() => onCancel(reservation.id)}
                className="btn-danger text-sm"
              >
                Cancelar
              </button>
            </>
          )}
          
          {/* Refund Button */}
          {canRefund && (
            <button
              onClick={() => setShowRefundModal(true)}
              className="bg-orange-600 hover:bg-orange-700 text-white font-medium py-1 px-3 rounded text-sm transition-colors flex items-center space-x-1"
            >
              <RotateCcw className="h-4 w-4" />
              <span>Cancelar & Reembolsar</span>
            </button>
          )}
        </div>
      </div>

      {/* Refund Confirmation Modal */}
      {showRefundModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-orange-100 rounded-full p-2">
                <RotateCcw className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Confirmar Reembolso
              </h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Tem a certeza que pretende cancelar esta reserva e reembolsar a taxa de €{reservation.payment_amount?.toFixed(2)}? 
              Esta ação não pode ser desfeita.
            </p>

            {refundError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                <div className="flex items-center space-x-2">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <span className="text-sm text-red-700">{refundError}</span>
                </div>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={() => setShowRefundModal(false)}
                disabled={isRefunding}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded text-sm transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleRefund}
                disabled={isRefunding}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 px-4 rounded text-sm transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                {isRefunding ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Processando...</span>
                  </>
                ) : (
                  <span>Confirmar Reembolso</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationCard;
